import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, Heart, Shield, Users } from 'lucide-react';

const PreviewOurStory = () => {
  const [isVisible, setIsVisible] = useState({});

  useEffect(() => {
    window.scrollTo(0, 0);
    
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            setIsVisible(prev => ({ ...prev, [entry.target.id]: true }));
          }
        });
      },
      { threshold: 0.1 }
    );

    document.querySelectorAll('[data-animate]').forEach(el => observer.observe(el));
    return () => observer.disconnect();
  }, []);

  return (
    <div className="min-h-screen">
      {/* Hero Section - Simplified */}
      <section className="relative h-[60vh] flex items-center justify-center overflow-hidden">
        <div className="absolute inset-0">
          <img 
            src="https://images.unsplash.com/photo-1769698678497-c41f0ab47c3e?crop=entropy&cs=srgb&fm=jpg&q=85&w=2400"
            alt="Modern Medical Centre Building"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-spire-purple/90 to-spire-purple/70"></div>
        </div>
        
        <div className="relative max-w-5xl mx-auto px-6 lg:px-8 text-center z-10">
          <div className="animate-fade-in-up">
            <h1 className="text-5xl md:text-6xl font-bold text-white mb-6">
              Our Story
            </h1>
            <p className="text-xl md:text-2xl text-white/90 max-w-3xl mx-auto">
              Over a century of combined expertise dedicated to healthcare real estate in North America
            </p>
          </div>
        </div>
      </section>

      {/* Main Story Content */}
      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6 lg:px-8">
          <div 
            id="story-intro"
            data-animate
            className={`mb-16 ${isVisible['story-intro'] ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <h2 className="text-4xl font-bold text-gray-900 mb-6">Leading Healthcare Real Estate Consultants</h2>
            <p className="text-xl text-gray-700 leading-relaxed mb-6">
              Spire Group has established itself as North America's premier healthcare real estate consulting firm, with a proven track record of helping healthcare professionals secure ideal spaces for their practices.
            </p>
            <p className="text-lg text-gray-600 leading-relaxed">
              With over 100 years of combined experience, we've successfully represented over 3,800 healthcare providers across Alberta, British Columbia, and Saskatchewan in lease negotiations, site selection, and real estate advisory services.
            </p>
          </div>

          {/* Values Cards */}
          <div className="grid md:grid-cols-3 gap-8 mb-16">
            {[
              {
                icon: Heart,
                title: 'Patient-Centered',
                description: 'Every decision considers the ultimate impact on patient care and community health.'
              },
              {
                icon: Shield,
                title: 'Protecting Your Interests',
                description: 'We safeguard your financial and legal interests throughout every transaction.'
              },
              {
                icon: Users,
                title: 'Lasting Relationships',
                description: 'Building trust through personalized support and long-term partnerships.'
              }
            ].map((value, index) => (
              <div 
                key={index}
                id={`value-${index}`}
                data-animate
                className={`bg-gray-50 rounded-2xl p-8 hover:shadow-xl transition-all duration-500 hover:-translate-y-2 ${
                  isVisible[`value-${index}`] ? 'animate-fade-in-up' : 'opacity-0'
                }`}
                style={{ animationDelay: `${index * 150}ms` }}
              >
                <div className="w-14 h-14 rounded-xl bg-spire-purple/10 flex items-center justify-center mb-6">
                  <value.icon className="text-spire-purple" size={28} />
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{value.title}</h3>
                <p className="text-gray-600 leading-relaxed">{value.description}</p>
              </div>
            ))}
          </div>

          {/* Experience Section */}
          <div 
            id="experience"
            data-animate
            className={`mb-16 ${isVisible['experience'] ? 'animate-fade-in-up' : 'opacity-0'}`}
          >
            <h2 className="text-3xl font-bold text-gray-900 mb-6">Expertise That Matters</h2>
            <p className="text-lg text-gray-700 leading-relaxed mb-6">
              Our team specializes in serving healthcare professionals across multiple specialties including dentistry, family medicine, specialist practices, optometry, chiropractic care, and more. We understand the unique challenges and opportunities in healthcare real estate.
            </p>
            <p className="text-lg text-gray-700 leading-relaxed">
              From site selection and demographic analysis to lease negotiation and practice transitions, we provide comprehensive support tailored to the specific needs of healthcare professionals in North America.
            </p>
          </div>

          {/* Commitment Section */}
          <div 
            id="commitment"
            data-animate
            className={`bg-gradient-to-br from-spire-purple to-purple-900 rounded-3xl p-12 text-white ${
              isVisible['commitment'] ? 'animate-fade-in-up' : 'opacity-0'
            }`}
          >
            <h2 className="text-3xl font-bold mb-6">Our Commitment to You</h2>
            <p className="text-lg text-white/90 leading-relaxed mb-6">
              We believe every healthcare provider deserves expert guidance when making critical real estate decisions. Our personalized approach ensures you receive dedicated support throughout your entire journey.
            </p>
            <p className="text-lg text-white/90 leading-relaxed">
              With Spire Group, you gain more than a consultant—you gain a trusted partner invested in your success and the success of your practice.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 bg-gray-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-4xl font-bold text-gray-900 mb-6">Ready to Get Started?</h2>
          <p className="text-xl text-gray-600 mb-10">
            Let's discuss how we can help you achieve your healthcare real estate goals
          </p>
          <Link 
            to="/contact"
            className="inline-flex items-center px-8 py-4 bg-spire-purple text-white rounded-xl font-semibold text-lg hover:bg-purple-700 transition-all duration-300 hover:scale-105 shadow-xl"
          >
            Schedule a Consultation
            <ArrowRight className="ml-2" size={20} />
          </Link>
        </div>
      </section>

      {/* Preview Badge */}
      <div className="fixed bottom-4 right-4 bg-yellow-500 text-black px-6 py-3 rounded-full font-bold shadow-2xl z-50">
        🎨 PREVIEW MODE
      </div>
    </div>
  );
};

export default PreviewOurStory;